import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Play, Users, Server as ServerIcon, AlertTriangle,
  Cpu, Box, Globe, Loader2,
  Calendar, Shield, HardDrive, MemoryStick, Wifi,
} from 'lucide-react';
import ReactPlayer from 'react-player';
import DownloadSettings from './DownloadSettings';
import MicrosoftLoginModal from '../settings/MicrosoftLoginModal';
import { useServerStore } from '../../store/serverStore';
import { hostingAPI } from '../../lib/api';

/* ── Types ── */
interface MinecraftServer {
  id: string;
  name: string;
  description: string;
  version: string;
  modLoader: 'vanilla' | 'forge' | 'fabric';
  modLoaderVersion?: string;
  address?: string;
  port?: number;
  icon_url: string;
  banner_url?: string;
  video_background_url?: string;
  playerCount: number;
  tags: string[];
  mods?: { name: string; icon_url?: string }[];
}

interface HomeChannelProps {
  channelId?: string;
  serverId: string;
}

/* ── Helpers ── */
const loaderColor: Record<string, string> = {
  vanilla: 'text-gaming-accent',
  forge: 'text-orange-400',
  fabric: 'text-blue-400',
  quilt: 'text-purple-400',
};

const HomeChannel: React.FC<HomeChannelProps> = ({ serverId }) => {
  const [hasMinecraftServer, setHasMinecraftServer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mcServer, setMcServer] = useState<MinecraftServer | null>(null);
  const [showDownloadSettings, setShowDownloadSettings] = useState(false);
  const [showMicrosoftModal, setShowMicrosoftModal] = useState(false);
  const [pendingLaunch, setPendingLaunch] = useState(false);
  const [isElectron] = useState(() => !!window.api);

  /* Hosted MC server linked to this Erozion server */
  const [linkedHostedServer, setLinkedHostedServer] = useState<{
    id: string; name: string; status: string; server_type: string; mc_version: string;
    subdomain: string | null; allocation_ip: string | null; allocation_port: number | null;
    memory_mb: number; disk_mb: number;
  } | null>(null);

  /* Zustand server store — for generic server info & members */
  const { currentServer, members, fetchServerMembers } = useServerStore();

  const [launchState, setLaunchState] = useState<{
    isLaunching: boolean;
    message: string;
    error: string | null;
    progress: number;
    total: number;
    details?: { percentage?: number; step?: string; substep?: string | null; taskName?: string; taskPath?: string };
  }>({ isLaunching: false, message: '', error: null, progress: 0, total: 100 });

  /* ── Data fetching ── */
  useEffect(() => {
    if (!serverId) return;

    fetchServerMembers(serverId);

    const checkMinecraftServer = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Not authorized');

        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(
          `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft`,
          { headers },
        );

        setHasMinecraftServer(true);

        // Also try to fetch manifest for mod list
        let mods: { name: string; icon_url?: string }[] = [];
        try {
          const manifestRes = await axios.get(
            `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft/manifest`,
            { headers },
          );
          mods = (manifestRes.data?.mods || []).map((m: any) => ({
            name: m.name,
            icon_url: m.icon_url,
          }));
        } catch { /* manifest optional */ }

        setMcServer({
          id: data.id,
          name: data.name,
          description: data.description || '',
          version: data.version,
          modLoader: data.mod_loader as MinecraftServer['modLoader'],
          modLoaderVersion: data.mod_loader_version,
          address: data.address,
          port: data.port || 25565,
          icon_url: data.icon_url,
          banner_url: data.banner_url,
          video_background_url: data.video_background_url,
          playerCount: data.player_count || 0,
          tags: data.tags || [],
          mods,
        });
        setError(null);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setHasMinecraftServer(false);
        } else {
          console.error('Error checking Minecraft server:', err);
          setError('Failed to check Minecraft server');
        }
      } finally {
        setLoading(false);
      }
    };

    checkMinecraftServer();

    // Check for a linked hosted MC server
    const checkLinkedHostedServer = async () => {
      try {
        const { owned, shared } = await hostingAPI.getServers();
        const all = [...owned, ...shared];
        const linked = all.find((s: any) => s.erozion_server_id === serverId);
        setLinkedHostedServer(linked || null);
      } catch {
        // Not critical
      }
    };
    checkLinkedHostedServer();
  }, [serverId]);

  /* ── Electron event listeners ── */
  useEffect(() => {
    if (!window.api) return;
    const launchingCleanup = window.api.onMinecraftLaunching((data: any) => {
      setLaunchState(prev => ({ ...prev, isLaunching: true, message: data.message || 'Installing...', error: null, progress: data.progress || 0, total: data.total || 100, details: data.details }));
    });
    const launchedCleanup = window.api.onMinecraftLaunched((data: any) => {
      setLaunchState(prev => ({ ...prev, isLaunching: false, message: data.message || 'Game launched!', error: null, progress: 100, total: 100 }));
    });
    const errorCleanup = window.api.onMinecraftError((data: any) => {
      setLaunchState(prev => ({ ...prev, isLaunching: false, message: '', error: data.error || 'Launch failed', progress: 0, total: 100 }));
    });
    const closedCleanup = window.api.onMinecraftClosed(() => {});
    return () => {
      if (typeof launchingCleanup === 'function') launchingCleanup();
      if (typeof launchedCleanup === 'function') launchedCleanup();
      if (typeof errorCleanup === 'function') errorCleanup();
      if (typeof closedCleanup === 'function') closedCleanup();
    };
  }, []);

  /* ── Launch logic ── */
  const handleLaunchGame = async () => {
    if (!mcServer || !window.api) {
      if (!window.api) setLaunchState(prev => ({ ...prev, error: 'Le launcher est disponible uniquement dans l\'app Erozion Desktop.' }));
      return;
    }

    const accounts = await window.api.getMinecraftAccounts();
    if (!accounts || accounts.length === 0) { setPendingLaunch(true); setShowMicrosoftModal(true); return; }
    const currentUser = await window.api.getCurrentUser();
    if (!currentUser?.success) await window.api.setActiveMinecraftAccount(accounts[0].id);

    setLaunchState({ isLaunching: true, message: 'Préparation du lancement...', error: null, progress: 0, total: 100 });

    try {
      const token = localStorage.getItem('access_token');
      const userId = localStorage.getItem('user_id');
      if (!token || !userId) throw new Error('Non autorisé');

      const headers = { Authorization: `Bearer ${token}` };

      try { await axios.post(`${import.meta.env.VITE_API_URL}/servers/${serverId}/join`, {}, { headers }); }
      catch (joinErr: any) { const s = joinErr?.response?.status; const m = String(joinErr?.response?.data?.message || '').toLowerCase(); if (!(s === 400 && m.includes('already a member'))) throw joinErr; }

      const manifestRes = await axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft/manifest`, { headers });
      const manifest = manifestRes.data;

      const localProfilesRes = await window.api.profileList();
      if (!localProfilesRes?.success) throw new Error('Impossible de lire les profils locaux');

      const serverRes = await axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}`, { headers });
      const allProfiles = (localProfilesRes.data || []) as any[];
      const existing = allProfiles.find((p: any) => p.source === 'server' && p.erozion_server_id === serverId) || null;
      const isOwner = !!serverRes?.data?.owner_id && serverRes.data.owner_id === userId;

      const nr = (v: any) => (v === 1 || v === true || v === '1');
      const localManifest = {
        manifest_version: manifest.manifest_version || 1,
        allow_user_mods: isOwner,
        server_description: manifest.server_description ?? manifest.description ?? mcServer.description ?? null,
        banner_url: mcServer.banner_url || null,
        video_background_url: mcServer.video_background_url || null,
        server: { address: manifest.server?.address || mcServer.address || '', port: manifest.server?.port || mcServer.port || 25565, auto_connect: manifest.server?.auto_connect ?? true },
        minecraft: { version: manifest.minecraft?.version || mcServer.version, loader: manifest.minecraft?.loader || mcServer.modLoader, loader_version: manifest.minecraft?.loader_version ?? mcServer.modLoaderVersion ?? null },
        mods: (manifest.mods || []).map((m: any) => ({ name: m.name, file_name: m.file_name, source: m.source, icon_url: m.icon_url, source_id: m.source_id, version_id: m.version_id, download_url: m.download_url, sha1: m.sha1, size: m.size, required: nr(m.required) })),
        resourcePacks: (manifest.resourcePacks || []).map((p: any) => ({ name: p.name, file_name: p.file_name, source: p.source, source_id: p.source_id, version_id: p.version_id, download_url: p.download_url, sha1: p.sha1, size: p.size })),
        shaderPacks: (manifest.shaderPacks || []).map((s: any) => ({ name: s.name, file_name: s.file_name, source: s.source, source_id: s.source_id, version_id: s.version_id, download_url: s.download_url, sha1: s.sha1, size: s.size })),
      };

      let profileIdToLaunch: string;
      if (!existing) {
        const createRes = await window.api.profileCreate({ name: manifest.name || mcServer.name, source: 'server', erozion_server_id: serverId, ...localManifest });
        if (!createRes?.success || !createRes?.data?.id) throw new Error(createRes?.error || 'Échec de création du profil');
        profileIdToLaunch = createRes.data.id;
      } else {
        profileIdToLaunch = existing.id;
        const syncRes = await window.api.profileSync(existing.id, localManifest);
        if (!syncRes?.success) throw new Error(syncRes?.error || 'Échec de synchronisation du profil');
      }

      const result = await window.api.launchProfile(profileIdToLaunch);
      if (!result.success) setLaunchState(prev => ({ ...prev, isLaunching: false, message: '', error: result.error || 'Failed to launch', progress: 0, total: 100 }));
    } catch (err) {
      setLaunchState(prev => ({ ...prev, isLaunching: false, message: '', error: err instanceof Error ? err.message : 'Une erreur est survenue', progress: 0, total: 100 }));
    }
  };

  const dismissError = () => setLaunchState(prev => ({ ...prev, error: null }));
  const dismissSuccess = () => setLaunchState(prev => ({ ...prev, message: '' }));

  /* ── Derived data ── */
  const onlineMembers = useMemo(() => members.filter(m => m.user?.status === 'online'), [members]);
  const displayMembers = members.slice(0, 14);
  const serverName = currentServer?.name || mcServer?.name || 'Serveur';
  const serverDesc = currentServer?.description || mcServer?.description || '';
  const createdAt = currentServer?.created_at ? new Date(currentServer.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long' }) : null;

  /* ── Loading / Error / Empty states ── */
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-text-muted animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-text-primary mb-1">Erreur</h2>
          <p className="text-sm text-text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     GENERIC HOME — Server without Minecraft
     ═══════════════════════════════════════════════════════════ */
  if (!hasMinecraftServer || !mcServer) {
    return (
      <div className="h-full overflow-y-auto gaming-scrollbar">
        {/* Hero */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <div className="absolute inset-0">
            {currentServer?.icon_url ? (
              <img src={currentServer.icon_url} className="w-full h-full object-cover blur-2xl scale-125 opacity-40" alt="" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gaming-accent/20 via-purple-900/30 to-bg-primary" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/60 to-bg-primary/30" />
          </div>
          <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gaming-accent/40 to-transparent" />

          {/* Hero content — left-aligned identity */}
          <div className="relative z-10 h-full flex items-end px-8 pb-8">
            <div className="flex items-center gap-5 flex-1 min-w-0">
              {currentServer?.icon_url ? (
                <div className="w-18 h-18 rounded-2xl overflow-hidden border-2 border-white/15 shadow-2xl shadow-black/50 ring-4 ring-white/5 flex-shrink-0" style={{ width: '4.5rem', height: '4.5rem' }}>
                  <img src={currentServer.icon_url} className="w-full h-full object-cover" alt="" />
                </div>
              ) : (
                <div className="w-18 h-18 rounded-2xl bg-bg-tertiary border-2 border-white/15 flex items-center justify-center shadow-2xl shadow-black/50 ring-4 ring-white/5 flex-shrink-0" style={{ width: '4.5rem', height: '4.5rem' }}>
                  <ServerIcon className="w-8 h-8 text-text-muted" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl lg:text-4xl font-black text-white mb-1 leading-tight tracking-tight truncate">
                  {serverName}
                </h1>
                <div className="flex items-center gap-3 text-sm text-text-secondary flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> {members.length} membre{members.length > 1 ? 's' : ''}
                  </span>
                  {onlineMembers.length > 0 && (
                    <span className="flex items-center gap-1.5 text-gaming-accent">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      {onlineMembers.length} en ligne
                    </span>
                  )}
                  {createdAt && (
                    <span className="flex items-center gap-1.5 text-text-muted">
                      <Calendar className="w-3.5 h-3.5" /> {createdAt}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content — two columns */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex gap-6 flex-col lg:flex-row">
            {/* Left — About */}
            <div className="flex-1 space-y-6 min-w-0">
              {serverDesc && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <ServerIcon className="w-4 h-4 text-gaming-accent" />
                    <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">À propos</h2>
                  </div>
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
                    <p className="text-sm text-text-secondary leading-relaxed">{serverDesc}</p>
                  </div>
                </section>
              )}

              {/* Stats */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-gaming-accent" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Statistiques</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-gaming-accent/40 transition-all duration-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-gaming-accent" />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-text-primary">{members.length}</p>
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">Membres</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-gaming-accent/40 transition-all duration-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-gaming-accent" />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gaming-accent">{onlineMembers.length}</p>
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">En ligne</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Linked hosted MC server widget */}
              {linkedHostedServer && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <HardDrive className="w-4 h-4 text-cyan-400" />
                    <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Erozion Go</h2>
                  </div>
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5 hover:border-cyan-500/30 transition-all duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-bold text-text-primary">{linkedHostedServer.name}</h3>
                        <p className="text-[10px] text-text-muted capitalize mt-0.5">
                          {linkedHostedServer.server_type} · {linkedHostedServer.mc_version}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        linkedHostedServer.status === 'online'
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : linkedHostedServer.status === 'starting' || linkedHostedServer.status === 'stopping'
                          ? 'bg-yellow-500/15 text-yellow-400'
                          : 'bg-text-muted/10 text-text-muted'
                      }`}>
                        {linkedHostedServer.status === 'online' ? 'En ligne' :
                         linkedHostedServer.status === 'offline' ? 'Hors ligne' :
                         linkedHostedServer.status === 'starting' ? 'Démarrage...' :
                         linkedHostedServer.status === 'stopping' ? 'Arrêt...' :
                         linkedHostedServer.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-[10px] text-text-muted">
                      <span className="flex items-center gap-1">
                        <Wifi className="w-3 h-3" />
                        {linkedHostedServer.subdomain
                          ? `${linkedHostedServer.subdomain}.mc.erozion.net`
                          : `${linkedHostedServer.allocation_ip || '—'}:${linkedHostedServer.allocation_port || '—'}`}
                      </span>
                      <span className="flex items-center gap-1">
                        <MemoryStick className="w-3 h-3" />
                        {linkedHostedServer.memory_mb} MB
                      </span>
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Right — Members sidebar */}
            <div className="w-full lg:w-80 flex-shrink-0">
              {displayMembers.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-gaming-accent" />
                    <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Membres</h2>
                    <span className="text-[10px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded-full">{members.length}</span>
                  </div>
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 space-y-1">
                    {displayMembers.map(m => (
                      <div key={m.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-bg-tertiary/50 transition-colors">
                        <div className="relative flex-shrink-0">
                          {m.user?.avatar_url ? (
                            <img src={m.user.avatar_url} className="w-8 h-8 rounded-full object-cover" alt="" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center text-[10px] font-bold text-text-muted">
                              {(m.user?.username || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-bg-secondary ${m.user?.status === 'online' ? 'bg-emerald-400' : 'bg-text-muted/30'}`} />
                        </div>
                        <span className="text-xs text-text-secondary font-medium flex-1 truncate">{m.user?.username || '???'}</span>
                        {m.role === 'owner' && (
                          <Shield className="w-3.5 h-3.5 text-gaming-accent flex-shrink-0" />
                        )}
                      </div>
                    ))}
                    {members.length > displayMembers.length && (
                      <p className="text-[10px] text-text-muted text-center pt-2">
                        +{members.length - displayMembers.length} autres
                      </p>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     MINECRAFT HOME — Server with Minecraft
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="h-full overflow-y-auto gaming-scrollbar">

      {/* Error toast */}
      {launchState.error && (
        <div className="fixed top-12 right-4 z-[200] bg-red-900/80 backdrop-blur-md border border-red-700/60 rounded-xl p-4 w-96 shadow-2xl shadow-red-900/20">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-red-300 mb-0.5">Erreur de lancement</h3>
              <p className="text-xs text-red-200/80 leading-relaxed">{launchState.error}</p>
            </div>
            <button onClick={dismissError} className="text-red-400 hover:text-red-300 text-lg leading-none">&times;</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showDownloadSettings && (
        <div className="fixed inset-0 flex items-center justify-center z-[200] bg-black/60 backdrop-blur-sm">
          <DownloadSettings onClose={() => setShowDownloadSettings(false)} />
        </div>
      )}
      {showMicrosoftModal && (
        <MicrosoftLoginModal
          onSuccess={async () => { setShowMicrosoftModal(false); if (pendingLaunch) { setPendingLaunch(false); await handleLaunchGame(); } }}
          onClose={() => { setShowMicrosoftModal(false); setPendingLaunch(false); }}
        />
      )}

      {/* ── HERO ── */}
      <section className="relative h-72 lg:h-80 overflow-hidden">
        <div className="absolute inset-0">
          {mcServer.video_background_url ? (
            <ReactPlayer
              url={mcServer.video_background_url}
              playing loop muted
              width="100%" height="100%"
              onError={(e: any) => console.error('Video error:', e)}
              style={{ position: 'absolute', top: 0, left: 0 }}
              config={{ file: { attributes: { style: { objectFit: 'cover', width: '100%', height: '100%' } } } }}
            />
          ) : mcServer.banner_url ? (
            <img src={mcServer.banner_url} className="w-full h-full object-cover" alt="" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-orange-900/50 via-bg-secondary to-bg-primary" />
          )}
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/50 to-transparent" />
        </div>

        {/* Bottom accent */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gaming-accent/40 to-transparent z-20" />

        {/* Hero content — left-aligned identity, right-aligned play */}
        <div className="relative z-10 h-full flex items-end px-8 pb-8">
          <div className="flex items-center gap-5 flex-1 min-w-0">
            {/* Icon */}
            <div className="w-18 h-18 rounded-2xl overflow-hidden border-2 border-white/15 shadow-2xl shadow-black/50 ring-4 ring-gaming-accent/10 bg-bg-tertiary flex-shrink-0" style={{ width: '4.5rem', height: '4.5rem' }}>
              <img
                src={mcServer.icon_url}
                alt={mcServer.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
            {/* Name + meta */}
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl lg:text-4xl font-black text-white mb-1 leading-tight tracking-tight truncate">
                {mcServer.name}
              </h1>
              <div className="flex items-center gap-3 text-sm text-white/60 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-gaming-accent font-semibold">
                  <Cpu className="w-3.5 h-3.5" /> {mcServer.modLoader !== 'vanilla' ? mcServer.modLoader : 'Ce jeu'} · {mcServer.version}
                </span>
                <span className="flex items-center gap-1.5 text-gaming-accent">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  {mcServer.playerCount} en ligne
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {members.length} membres
                </span>
                {mcServer.mods && mcServer.mods.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Box className="w-3.5 h-3.5" /> {mcServer.mods.length} mods
                  </span>
                )}
              </div>
            </div>
            {/* Play button — right side */}
            <div className="flex items-center flex-shrink-0">
              <button
                onClick={handleLaunchGame}
                disabled={launchState.isLaunching || !isElectron}
                className="inline-flex items-center justify-center gap-2 px-8 py-2.5 rounded-lg bg-gaming-accent text-black font-bold text-sm hover:bg-gaming-accent/80 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-gaming-accent/25 min-w-[8.5rem]"
              >
                {launchState.isLaunching ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Installation...</>
                ) : (
                  <><Play className="w-4 h-4 fill-current" /> Jouer</>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── LAUNCH PROGRESS STRIP ── */}
      {launchState.isLaunching && !launchState.error && typeof launchState.progress === 'number' && (
        <div className="border-b border-border-color bg-bg-secondary/60 backdrop-blur-sm px-8 py-2">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between text-[10px] text-text-muted mb-1">
              <span className="truncate max-w-[85%]">{launchState.message}</span>
              <span>{launchState.progress}%</span>
            </div>
            <div className="h-1 bg-bg-primary rounded-full overflow-hidden">
              <div
                className="h-full bg-gaming-accent transition-all duration-300 rounded-full"
                style={{ width: `${launchState.progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── LAUNCH ERROR STRIP ── */}
      {launchState.error && (
        <div className="border-b border-red-500/20 bg-red-500/5 px-8 py-2">
          <div className="max-w-6xl mx-auto">
            <div className="text-[10px] text-red-400 font-semibold mb-1">Erreur de lancement</div>
            <pre className="text-[9px] text-red-300 bg-bg-primary rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap break-all gaming-scrollbar">
              {launchState.error}
            </pre>
            <button onClick={dismissSuccess} className="mt-1.5 text-[10px] text-text-muted hover:text-text-primary transition-colors">
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* ── CONTENT ── */}
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Desktop required notice */}
        {!isElectron && (
          <div className="flex items-center gap-3 p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-6">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-xs text-amber-200">
              Pour lancer ce jeu, téléchargez et utilisez l'application Erozion Desktop.
            </p>
          </div>
        )}

        {/* Two-column layout */}
        <div className="flex gap-6 flex-col lg:flex-row">

          {/* Left — About, Info & Mods */}
          <div className="flex-1 space-y-6 min-w-0">

            {/* Description */}
            {mcServer.description && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ServerIcon className="w-4 h-4 text-gaming-accent" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">À propos</h2>
                </div>
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
                  <p className="text-sm text-text-secondary leading-relaxed">{mcServer.description}</p>
                  {mcServer.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-border-color/50">
                      {mcServer.tags.map(tag => (
                        <span key={tag} className="px-2.5 py-1 bg-bg-tertiary/80 border border-border-color/40 rounded-full text-[10px] text-text-muted font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Server info cards */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-gaming-accent" />
                <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Infos serveur</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-gaming-accent/30 transition-all duration-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                      <Cpu className="w-5 h-5 text-gaming-accent" />
                    </div>
                    <div>
                      <p className={`text-sm font-bold capitalize ${loaderColor[mcServer.modLoader] || 'text-text-primary'}`}>{mcServer.modLoader}{mcServer.modLoaderVersion ? ` ${mcServer.modLoaderVersion}` : ''}</p>
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">Mod Loader</p>
                    </div>
                  </div>
                </div>
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-gaming-accent/30 transition-all duration-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-gaming-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-text-primary">{mcServer.version}</p>
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">Version</p>
                    </div>
                  </div>
                </div>
                {mcServer.address && (
                  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-gaming-accent/30 transition-all duration-200 col-span-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gaming-accent/10 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-gaming-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-text-primary font-mono">{mcServer.address}{mcServer.port && mcServer.port !== 25565 ? ':' + mcServer.port : ''}</p>
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">Adresse</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Linked hosted server status */}
            {linkedHostedServer && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <HardDrive className="w-4 h-4 text-cyan-400" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Erozion Go</h2>
                </div>
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 hover:border-cyan-500/30 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${linkedHostedServer.status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-text-muted/30'}`} />
                      <div>
                        <p className="text-xs font-medium text-text-primary">{linkedHostedServer.name}</p>
                        <p className="text-[10px] text-text-muted">
                          {linkedHostedServer.status === 'online' ? 'En ligne' : linkedHostedServer.status === 'offline' ? 'Hors ligne' : linkedHostedServer.status}
                          {' · '}{linkedHostedServer.memory_mb} MB RAM
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] text-text-muted font-mono">
                      {linkedHostedServer.subdomain
                        ? `${linkedHostedServer.subdomain}.mc.erozion.net`
                        : `${linkedHostedServer.allocation_ip || ''}:${linkedHostedServer.allocation_port || ''}`}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* Modpack */}
            {(mcServer.mods?.length ?? 0) > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Box className="w-4 h-4 text-gaming-accent" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Modpack</h2>
                  <span className="text-[10px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded-full">
                    {mcServer.mods!.length}
                  </span>
                </div>
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {mcServer.mods!.slice(0, 10).map((mod, i) => (
                      <div key={i} className="flex items-center gap-2.5 rounded-lg p-2 hover:bg-bg-tertiary/50 transition-colors">
                        {mod.icon_url ? (
                          <img src={mod.icon_url} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" alt="" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                            <Box className="w-3.5 h-3.5 text-text-muted" />
                          </div>
                        )}
                        <span className="text-xs text-text-secondary font-medium truncate">{mod.name}</span>
                      </div>
                    ))}
                  </div>
                  {mcServer.mods!.length > 10 && (
                    <p className="text-[10px] text-text-muted text-center mt-3 pt-3 border-t border-border-color/50">
                      +{mcServer.mods!.length - 10} autres mods
                    </p>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Right — Members sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0">
            {displayMembers.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gaming-accent" />
                  <h2 className="text-sm font-bold text-text-primary uppercase tracking-wide">Membres</h2>
                  <span className="text-[10px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded-full">
                    {members.length}
                  </span>
                </div>
                <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 space-y-1">
                  {displayMembers.map(m => (
                    <div key={m.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-bg-tertiary/50 transition-colors">
                      <div className="relative flex-shrink-0">
                        {m.user?.avatar_url ? (
                          <img src={m.user.avatar_url} className="w-8 h-8 rounded-full object-cover" alt="" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center text-[10px] font-bold text-text-muted">
                            {(m.user?.username || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-bg-secondary ${m.user?.status === 'online' ? 'bg-emerald-400' : 'bg-text-muted/30'}`} />
                      </div>
                      <span className="text-xs text-text-secondary font-medium flex-1 truncate">{m.user?.username || '???'}</span>
                      {m.role === 'owner' && (
                        <Shield className="w-3.5 h-3.5 text-gaming-accent flex-shrink-0" />
                      )}
                    </div>
                  ))}
                  {members.length > displayMembers.length && (
                    <p className="text-[10px] text-text-muted text-center pt-2">
                      +{members.length - displayMembers.length} autres
                    </p>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



export default HomeChannel;
